import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import makeStyles from '@mui/styles/makeStyles';
import { GaussianBlurBackgroundProcessor } from '@twilio/video-processors';
import { useMediaStreamTrack, useIsTrackEnabled, useVideoTrackDimensions, useVideoContext } from 'hooks';
import Styles from './styles';

const useStyles = makeStyles(Styles);

export default function VideoTrack({ track, isLocal, priority }) {
  const classes = useStyles();
  const ref = useRef(null);
  const mediaStreamTrack = useMediaStreamTrack(track);
  const isVideoEnabled = useIsTrackEnabled(track);
  const dimensions = useVideoTrackDimensions(track);
  const videoContext = useVideoContext();
  const isPortrait = (dimensions?.height ?? 0) > (dimensions?.width ?? 0);

  useEffect(() => {
    if (ref.current && track) {
      const el = ref.current;
      el.muted = true;
      if (track.setPriority && priority) {
        track.setPriority(priority);
      }
      if (videoContext.settings.blurInput) {
        const blurBackground = new GaussianBlurBackgroundProcessor({
          assetsPath: '/assets/twilio/',
          maskBlurRadius: 10,
          blurFilterRadius: 5,
        });
        blurBackground.loadModel().then(() => {
          track.attach(el);
          if (!track.processor) {
            track.addProcessor(blurBackground);
          }
        });
      } else {
        track.attach(el);
      }
      return () => {
        track.detach(el);

        // This addresses a Chrome issue where the number of WebMediaPlayers is limited.
        // See: https://github.com/twilio/twilio-video.js/issues/1528
        el.srcObject = null;
        if (track.setPriority && priority) {
          // Passing `null` to setPriority will set the track's priority to that which it was published with.
          track.setPriority(null);
        }
      };
    }
  }, [track, isVideoEnabled, ref.current, priority]);
  const isFrontFacing = mediaStreamTrack?.getSettings().facingMode !== 'environment';

  return !isVideoEnabled ? null : (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      ref={ref}
      className={cn(classes.videoElement, {
        [classes.mirrored]: isLocal && isFrontFacing,
        [classes.portrait]: isPortrait,
      })}
    />
  );
}

VideoTrack.propTypes = {
  track: PropTypes.objectOf(PropTypes.any),
  isLocal: PropTypes.bool.isRequired,
  priority: PropTypes.string,
};

VideoTrack.defaultProps = {
  track: null,
  priority: null,
};
